import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {IBigWinLevel} from "../server/IData";
import {Game} from "../Game";
import {Sound} from "../sound/Sound";
import {BigWinParticles} from "./BigWinParticles";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {GameModeNames} from "../server/GameModeNames";
import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class BigWin extends PIXI.Container implements StageLayer {

    private _bigWinAnimation: PIXI.spine.Spine;
    private _particles: BigWinParticles;
    private _scaredCharacterAnim: PIXI.spine.Spine;
    private _weaponCharacterAnim: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "BigWin";
        this.init();
    }

    public playBigWin(flowStateController: ISimplePresentationRunnerController): void {
        const levels: IBigWinLevel[] = Game.model.getBigWinLevels(),
            tl: gsap.core.Timeline = gsap.timeline({
                delay: 1.3, // #175
                onComplete: () => {
                    Game.soundPlayer.stopSound(Sound.BIG_WIN_OUTRO);
                    flowStateController.finish();
                }
            }),
            winAmount: number = Game.model.gameData.spinWin;

        this.precalculateScale(winAmount);

        tl.add(() => {
            Game.countUp.getReady(540);
            Game.blackBg.show(0.8);
            Game.soundPlayer.handleBGMusic(Game.getCurrentGameMode(), false);
            Game.soundPlayer.stopSound(Sound.MAIN_BG_AMBIENCE);
        });
        for (let i: number = 0; i < levels.length; i++) {
            const level: IBigWinLevel = levels[i];

            tl.add(() => {
                flowStateController.setSkipCallback(() => {
                    tl.seek(level.name + "_end", false);
                });
            });

            tl.add([
                () => {
                    this.createCharacterSpineObj(i);
                    this.playCharacterAnimation();
                    Game.shakeScreen();
                },
                this.playLevelAnimation(level)
            ]);

            if (i === levels.length-1){
                tl.add(()=>{
                    Game.soundPlayer.playSound(Sound.BIG_WIN_OUTRO, 1);
                })
            }

            tl.addLabel(level.name + "_end");
            tl.add(() => {
                flowStateController.setSkipCallback();
            });
        }

        // #344
        tl.add(() => {
            Game.countUp.stopAt(winAmount);

            this._particles.stop();
            this._particles.visible = false;
        });
        tl.to(Game.countUp.scale, {
            x: 1.25,
            y: 1.25,
            duration: 0.2
        });
        tl.to(Game.countUp.scale, {
            x: 1,
            y: 1,
            duration: 0.1
        });

        tl.add(() => {
            Game.keypadPlugin.setWin(Game.model.gameData.accumulatedRoundWin, true, Game.isWinBelowStake());

            const currentMode: string = Game.getCurrentGameMode();
            let fadeInAndStartMusic: string;

            if (currentMode === GameModeNames.MAYHEM) {
                fadeInAndStartMusic = Sound.MAYHEM_LOOP;
            } else if (currentMode === GameModeNames.SUPER_FREESPIN) {
                fadeInAndStartMusic = Sound.SFS_LOOP;
            } else if (currentMode === GameModeNames.FREESPIN) {
                fadeInAndStartMusic = Sound.FS_LOOP;
            } else {
                fadeInAndStartMusic = Sound.MAIN_GAME_MUSIC;
            }

            if (fadeInAndStartMusic === Sound.MAIN_GAME_MUSIC){
                Game.soundPlayer.fadeInAndStartSound({
                    name: Sound.AS_SNARE,
                    from: 0,
                    to: 0,
                    duration: 4000,
                    isResume: true,
                    repeat: Number.MAX_VALUE
                });
    
                Game.soundPlayer.fadeInAndStartSound({
                    name: Sound.SNARE_PLAYER,
                    from: 0,
                    to: 0,
                    duration: 2000,
                    isResume: true,
                    repeat: Number.MAX_VALUE
                });
            } else{
                Game.soundPlayer.stopSound(Sound.AS_SNARE);
                Game.soundPlayer.stopSound(Sound.SNARE_PLAYER);
            }

            Game.soundPlayer.fadeInAndStartSound({
                name: fadeInAndStartMusic,
                from: 0,
                to: 1,
                duration: 2000,
                repeat: Number.MAX_VALUE,
                isResume: true
            });

            if (currentMode === GameModeNames.NORMAL) {
                Game.soundPlayer.playSound(Sound.MAIN_BG_AMBIENCE, 1, Number.MAX_VALUE);
            }
        });

        tl.to(this, {
            duration: 0.6
        });

        tl.add(this.end());
    }

    public playMXWinEndAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(()=>{
            this._bigWinAnimation.state.setAnimation(1, "wincap/wincap_in", false)
        });
        
        tl.add(()=>{
            const animationStr: string = "wincap/effect" + (NolimitApplication.isLandscape ? "_landscape": "_portrail");
            this._bigWinAnimation.state.setAnimation(0, animationStr, true);
        })
        return tl;
    }

    private playLevelAnimation(level: IBigWinLevel): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    Game.soundPlayer.stopSound((Sound as any)[Game.BIG_WIN_LVL_TO_SOUND[level.name]]);
                }
            }),
            playedBet: number = Game.model.gameData.playedBetValue,
            countFrom: number = level.from * playedBet,
            countTo: number = level.to * playedBet,
            id: number = level.id,
            duration: number = level.duration;

        tl.add([
            () => {
                Game.soundPlayer.playSound((Sound as any)[Game.BIG_WIN_LVL_TO_SOUND[level.name]], 1);

                if (id === 1) {
                    this._bigWinAnimation.skeleton.setToSetupPose();

                    this._bigWinAnimation.alpha = 1;
                    this._bigWinAnimation.visible = true;
                }

                this._particles.alpha = 1;
                this._particles.visible = true;
                this._particles.play(id - 1);
            },
            Game.createTimelineForAnimation(this._bigWinAnimation, 0, "lvl" + id + "/lvl" + id + "_in").eventCallback("onComplete", () => {
                this._bigWinAnimation.autoUpdate = true;
                this._bigWinAnimation.state.setAnimation(0, "lvl" + id + "/lvl" + id + "_loop", true);
            }),
            Game.countUp.getCountAnimation(countFrom, countTo, duration)
        ]);

        return tl;
    }

    private precalculateScale(endValue: number): void {
        Game.countUp.stopAt(0);
        Game.countUp.preCalculateScale(0, endValue);
    }

    private end(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.5;

        tl.add(() => {
            this._scaredCharacterAnim.visible = false;
            this._weaponCharacterAnim.visible = false;
        });

        tl.to([Game.countUp, Game.blackBg, this._bigWinAnimation, this._scaredCharacterAnim, this._weaponCharacterAnim], {
            duration: duration,
            alpha: 0
        });

        tl.add(() => {
            Game.countUp.visible = false;
            Game.blackBg.visible = false;
            this._bigWinAnimation.visible = false;
            // this._scaredCharacterAnim.visible = false;
            // this._weaponCharacterAnim.visible = false;
            this._bigWinAnimation.state.clearTracks();
            this._scaredCharacterAnim.state.clearTracks();
            this._weaponCharacterAnim.state.clearTracks();
        });

        return tl;
    }

    private init(): void {
        this._bigWinAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("bigWins"));
        this._bigWinAnimation.visible = false;
        this._bigWinAnimation.position.set(360, 330);

        this._particles = new BigWinParticles();
        this._particles.visible = false;
        this._particles.position.set(360, 700);

        this.addChild(this._bigWinAnimation, this._particles);
    }

    private createCharacterSpineObj(level: number): void {
        if (this._scaredCharacterAnim && this._weaponCharacterAnim && this._particles) {
            this.removeChildren(this.children.length - 3); // removing characters, particles
        }

        let randomScaredCharPick: number = MathHelper.randomIntInRange(1, 4);
        let randomwWeaponCharPick: number = MathHelper.randomIntInRange(1, 4);

        // avoid same character for scared and weapon carry
        if (randomScaredCharPick === randomwWeaponCharPick) {
            randomScaredCharPick = (randomScaredCharPick % 4) + 1;
        }

        let randomScaredCharStr: string = "C" + randomScaredCharPick;
        let randomWeaponCharStr: string = "C" + randomwWeaponCharPick;

        let leftCharX: number = 250, leftCharY: number = 175, rightCharX: number = 480, rightCharY: number = 175;

        if (level === 4) {
            leftCharX = 145;
            rightCharX = 570;
        }

        this._scaredCharacterAnim = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset(randomScaredCharStr));
        this._scaredCharacterAnim.position.set(leftCharX, leftCharY);
        this._scaredCharacterAnim.alpha = 0;
        this._scaredCharacterAnim.skeleton.setSkinByName("base");

        this._weaponCharacterAnim = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset(randomWeaponCharStr));
        this._weaponCharacterAnim.position.set(rightCharX, rightCharY);
        this._weaponCharacterAnim.alpha = 0;
        this._weaponCharacterAnim.skeleton.setSkinByName("base");

        if (MathHelper.randomIntInRange(0, 1)) {
            this._scaredCharacterAnim.scale.set(-1, 1);
            this._weaponCharacterAnim.scale.set(-1, 1);

            this._scaredCharacterAnim.position.set(rightCharX, rightCharY);
            this._weaponCharacterAnim.position.set(leftCharX, leftCharY);
        }

        this.addChild(this._scaredCharacterAnim, this._weaponCharacterAnim, this._particles);
    }

    private playCharacterAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.2;

        tl.add(() => {
            const randomWeaponPick: number = MathHelper.randomIntInRange(1, 7);
            this._scaredCharacterAnim.state.setAnimation(0, "bigwin_scared", true);
            this._weaponCharacterAnim.state.setAnimation(0, "bigwin_weapon_loop_" + randomWeaponPick, true);
        })

        tl.to([this._scaredCharacterAnim, this._weaponCharacterAnim], {
            duration,
            alpha: 1
        }, "<")

        return tl;
    }
}